/**
 * Organization (Traxo CONNECT) Routes
 * @author      Jeff Adams <jeff@tinyfly.com>
 * @copyright   Traxo, Inc. 2019
 */

import { DateTime } from 'luxon';
import { handleErrors } from '@/utils/errors.js';
import { store } from '@/state/store.js';
import { TripsApi } from '@/services/api-trips.js';
import { OrgsDashboardApi } from '@/services/api-org-dashboard.js';
import { useImportsStore } from '@/stores/imports';

let isTravelDefault = false;

export const orgRoutes = [
  {
    path: '/orgs',
    name: 'org.index',
    // TODO: check if there is a team id set and take them to that dashboard instead
    redirect: { name: 'teams.index' },
  },

  {
    path: '/orgs/:teamId',
    name: 'org.show',
    component: () => import(/* webpackChunkName: "wrapper" */ '@/views/wrapper.vue'),
    redirect: { name: 'org.dashboard.index' },
    children: [
      // DASHBOARD
      {
        path: 'dashboard',
        name: 'org.dashboard.index',
        component: () => import(/* webpackChunkName: "org-dashboard" */ '@/views/orgs/show.vue'),
        props: true,
        meta: {
          permission: 'read-dashboard',
          async beforeResolve(to, from, next) {
            OrgsDashboardApi.index()
              .then((r) => {
                to.params.trips = r.trips?.length || 0;
                next();
              })
              .catch((err) => {
                handleErrors(err, next, 'Dashboard');
              });
          },
        },
      },

      // Travel (trips & segments)
      {
        path: 'travel',
        name: 'org.travel.index',
        component: () => import(/* webpackChunkName: "travel" */ '@/views/travel/index.vue'),
        props: (route) => {
          return {
            type: route.query?.type?.split(','),
          };
        },
        meta: {
          permission: 'browse-trips',
          storeQuery: true,
          beforeResolve(to, from, next) {
            const now = DateTime.utc();

            // pull out any applied filters: ignoring type and limit
            let queryKeys = Object.keys(to.query).filter((key) => {
              return ['limit', 'type'].indexOf(key) === -1;
            });

            // Set a default view only if there aren't any filters applied
            if (!queryKeys.length) {
              to.query.start_date = now.startOf('day').toISODate();
              to.query.end_date = now.endOf('day').toISODate();
              to.query.sort = 'start_date:asc';
              isTravelDefault = true;
              // Put these defaults in the query string in the browser
              next(to);
              return;
            }

            store
              .dispatch('travel/index', { query: to.query })
              .then((response) => {
                // No trips for the default time period
                if (!response?.data?.length && isTravelDefault) {
                  // So let's start today with no end date and re-request
                  to.query.start_date = now.startOf('day').toISODate();
                  delete to.query.end_date;
                  isTravelDefault = false;

                  next(to);
                  return;
                }

                next();
              })
              .catch((err) => {
                handleErrors(err, next, 'Travel');
              });
          },
        },
      },

      // Redirect from a particular segment id to the actual trip
      {
        path: 'segments/:segmentType/:segmentId',
        name: 'org.segments.show',
        meta: {
          async beforeResolve(to, from, next) {
            const { params } = to;
            try {
              const tripId = await TripsApi.getTripBySegment(params.segmentId, params.segmentType);
              if (tripId) {
                next({
                  name: 'org.trips.show',
                  params: { teamId: params.teamId, tripId: tripId },
                  hash: `#s${params.segmentId}`,
                });
              } else {
                next(false);
              }
            } catch (err) {
              handleErrors(err, next, 'Trip');
            }
          },
        },
      },

      {
        path: 'trips/:tripId',
        name: 'org.trips.show',
        props: true,
        component: () => import(/* webpackChunkName: "trips" */ '@/views/trips/show.vue'),
        meta: {
          permission: 'read-trips',
          async beforeResolve(to, from, next) {
            // store the from route so that within the component we
            // can return to that route on the delete action
            to.meta.from = from;
            try {
              to.params.trip = await store.dispatch('travel/trip/show', to.params.tripId);
              next();
            } catch (err) {
              handleErrors(err, next, 'Trip');
            }
          },
        },
      },

      // People (members)
      {
        path: 'people',
        name: 'org.members.index',
        component: () => import(/* webpackChunkName: "members" */ '@/views/members/index.vue'),
        meta: {
          permission: 'browse-members',
          storeQuery: true,
          beforeResolve(to, from, next) {
            // Set a default sort
            if (!to.query.sort) {
              to.query.sort = 'created:desc';
            }

            store
              .dispatch('members/index', { query: to.query })
              .then(() => {
                next();
              })
              .catch((err) => {
                handleErrors(err, next, 'People');
              });
          },
        },
        children: [
          {
            path: 'create',
            name: 'org.members.create',
            component: () => import(/* webpackChunkName: "members" */ '@/views/members/create.vue'),
            props: ({ params }) => {
              return {
                stage: params.stage || 'select',
              };
            },
            meta: {
              permission: 'add-members',
            },
          },
        ],
      },

      // People (bulk member upload)
      {
        path: 'people/imports/:id?',
        name: 'org.members.imports',
        component: () => import(/* webpackChunkName: "members-import" */ '@/views/members/import/import.vue'),
        meta: {
          permission: 'add-members',
          async beforeResolve(to, from, next) {
            let action = to.params.id ? 'members/imports/show' : 'members/imports/index';
            try {
              await store.dispatch(action, { id: to.params.id });
              next();
            } catch (err) {
              handleErrors(err, next, 'People Import');
            }
          },
        },
      },

      {
        path: 'people/:id',
        name: 'org.members.show',
        component: () => import(/* webpackChunkName: "members" */ '@/views/members/show.vue'),
        props: true,
        meta: {
          permission: 'read-members',
          beforeResolve(to, from, next) {
            store
              .dispatch('members/show', { id: to.params.id })
              .then((member) => {
                to.params.member = member;
                next();
              })
              .catch((err) => {
                handleErrors(err, next, 'Member');
              });
          },
        },
      },

      // REPORTS //
      {
        path: 'reports',
        name: 'org.reports.index',
        component: () => import(/* webpackChunkName: "reports" */ '@/views/reports/index.vue'),
        props: (route) => ({ query: route.query }),
        meta: {
          permission: 'add-reports',
          storeQuery: true,
          beforeResolve(to, from, next) {
            store
              .dispatch('reports/index', { query: to.query })
              .then(() => next())
              .catch((err) => handleErrors(err, next, 'Reports'));
          },
        },
        children: [
          {
            path: ':id(\\d+)',
            name: 'org.reports.show',
            component: () => import(/* webpackChunkName: "reports" */ '@/views/reports/show.vue'),
            props: (route) => ({ query: route.query, report: route.params.report }),
            meta: {
              permission: 'read-reports',
              beforeResolve(to, from, next) {
                store
                  .dispatch('reports/show', { id: to.params.id })
                  .then((report) => {
                    to.params.report = report;
                    next();
                  })
                  .catch((err) => {
                    handleErrors(err, next, 'Reports');
                  });
              },
            },
          },
        ],
      },

      // EMAILS
      {
        path: 'emails',
        name: 'org.emails.index',
        component: () => import(/* webpackChunkName: "emails" */ '@/views/emails/index.vue'),
        meta: {
          permission: 'browse-emails',
          storeQuery: true,
          beforeResolve(routeTo, routeFrom, next) {
            // Set a default sort
            if (!routeTo.query.sort) {
              routeTo.query.sort = 'created:desc';
            }

            store
              .dispatch('emails/index', { query: routeTo.query })
              .then(() => {
                next();
              })
              .catch((err) => {
                handleErrors(err, next, 'Emails');
              });
          },
        },
      },

      {
        path: 'emails/:id',
        name: 'org.emails.show',
        component: () => import(/* webpackChunkName: "emails" */ '@/views/emails/show.vue'),
        props: true,
        meta: {
          permission: 'read-emails',
          beforeResolve(to, from, next) {
            store
              .dispatch('emails/show', to.params.id)
              .then((email) => {
                to.params.email = email;
                to.params.fromRoute = from;
                next();
              })
              .catch((err) => {
                handleErrors(err, next, 'Email');
              });
          },
        },
      },

      {
        path: 'resources',
        name: 'org.resources.index',
        component: () => import(/* webpackChunkName: "resources" */ '@/views/resources/index.vue'),
        redirect: { name: 'org.data-explorer.index' },
        children: [
          {
            path: 'data-explorer',
            name: 'org.data-explorer.index',
            component: () => import(/* webpackChunkName: "resources" */ '@/views/resources/data-explorer/index.vue'),
            meta: {
              // currently the only thing on this page is creating downloads, we don't
              // list past downloads so no need to use a read or browse permission
              permission: 'add-downloads',
            },
          },
        ],
      },

      // IMPORTS
      {
        path: 'imports',
        component: () => import(/* webpackChunkName: "emails-imports" */ '@/views/imports/Imports.vue'),
        meta: {
          permission: 'add-imports',
          async beforeResolve(to, from, next) {
            const store = useImportsStore();

            // If we're coming from a child route, and we
            // already have data don't request new data
            if (from.path.includes('/imports') && store.imports?.length) {
              next();
              return;
            }

            await store.fetchImports();
            next();
          },
        },
        children: [
          {
            path: '',
            name: 'org.imports.index',
            component: () => import(/* webpackChunkName: "emails-imports" */ '@/views/imports/Index.vue'),
            redirect: { name: 'org.imports.new' },
          },
          {
            path: 'new',
            name: 'org.imports.new',
            component: () => import(/* webpackChunkName: "emails-imports" */ '@/views/imports/New.vue'),
            props: (route) => ({ type: route.query?.type }),
          },
          {
            path: 'new/preview',
            name: 'org.imports.preview',
            component: () => import(/* webpackChunkName: "emails-imports" */ '@/views/imports/Preview.vue'),
          },
          {
            path: 'new/complete',
            name: 'org.imports.complete',
            component: () => import(/* webpackChunkName: "emails-imports" */ '@/views/imports/Complete.vue'),
          },
        ],
      },

      // SETTINGS //
      // General
      {
        path: 'settings',
        name: 'org.settings',
        component: () => import(/* webpackChunkName: "wrapper" */ '@/views/wrapper.vue'),
        redirect: { name: 'org.settings.index' },
        meta: { permission: 'browse-settings' },
        children: [
          {
            path: 'general',
            name: 'org.settings.index',
            component: () => import(/* webpackChunkName: "settings" */ '@/views/settings/index.vue'),
            meta: {
              permission: 'read-organizations',
              beforeResolve(to, from, next) {
                store
                  .dispatch('me/showTeam', to.params.teamId)
                  .then(() => {
                    next();
                  })
                  .catch((err) => {
                    handleErrors(err, next, 'General Settings');
                  });
              },
            },
          },

          // Users
          {
            path: 'users',
            name: 'org.users.index',
            component: () => import(/* webpackChunkName: "users" */ '@/views/users/index.vue'),
            meta: {
              permission: 'browse-users',
              storeQuery: true,
              beforeResolve(to, from, next) {
                store
                  .dispatch('users/fetch', { query: to.query })
                  .then(() => {
                    next();
                  })
                  .catch((error) => {
                    handleErrors(error, next, 'Users');
                  });
              },
            },
            children: [
              {
                path: 'create',
                name: 'org.users.create',
                component: () => import(/* webpackChunkName: "users" */ '@/views/users/create.vue'),
                meta: { permission: 'add-users' },
                beforeEnter: (to, from, next) => {
                  // Don't allow user creation for sso accounts
                  if (store.getters['me/ssoEnabled']) {
                    let err = new Error('Forbidden');
                    err.response = { status: 404 };
                    return next(err);
                  }
                  return next();
                },
              },
              {
                path: ':id',
                name: 'org.users.show',
                component: () => import(/* webpackChunkName: "users" */ '@/views/users/show.vue'),
                meta: {
                  permission: 'read-users',
                  beforeResolve(to, from, next) {
                    store
                      .dispatch('users/read', { id: to.params.id })
                      .then((user) => {
                        to.params.user = user;
                        next();
                      })
                      .catch((err) => {
                        handleErrors(err, next, 'User');
                      });
                  },
                },
                props: (route) => ({ user: route.params.user }),
              },
            ],
          },

          // Integrations
          {
            path: 'integrations',
            name: 'org.integrations.index',
            component: () => import(/* webpackChunkName: "integrations" */ '@/views/integrations/index.vue'),
            meta: {
              permission: 'browse-integrations',
            },
          },

          // mailboxes
          {
            path: 'mailboxes',
            name: 'org.mailboxes.index',
            component: () => import(/* webpackChunkName: "mailboxes" */ '@/views/mailboxes/index.vue'),
            meta: {
              permission: 'browse-mailboxes',
              storeQuery: true,
              beforeResolve(to, from, next) {
                store
                  .dispatch('mailboxes/fetch', { query: to.query })
                  .then(() => next())
                  .catch((err) => {
                    handleErrors(err, next, 'Mailboxes');
                  });
              },
            },
            children: [
              {
                path: 'create',
                name: 'org.mailboxes.create',
                component: () => import(/* webpackChunkName: "mailboxes" */ '@/views/mailboxes/create.vue'),
                meta: { permission: 'add-mailboxes' },
              },
            ],
          },
          {
            path: 'mailboxes/:id',
            name: 'org.mailboxes.show',
            component: () => import(/* webpackChunkName: "mailboxes" */ '@/views/mailboxes/show.vue'),
            meta: {
              permission: 'read-mailboxes',
              beforeResolve(to, from, next) {
                store
                  .dispatch('mailboxes/read', { id: to.params.id })
                  .then((mailbox) => {
                    to.params.mailbox = mailbox;
                    next();
                  })
                  .catch((err) => {
                    handleErrors(err, next, 'Mailbox');
                  });
              },
            },
            props: (route) => ({ mailbox: route.params.mailbox }),
          },

          // Applications
          {
            path: 'apps',
            name: 'org.apps.index',
            component: () => import(/* webpackChunkName: "apps" */ '@/views/applications/index.vue'),
            meta: {
              permission: 'browse-applications',
              storeQuery: true,
              beforeResolve(to, from, next) {
                // Set a default sort
                if (!to.query.sort) {
                  to.query.sort = 'created:desc';
                }

                store
                  .dispatch('applications/fetch', { query: to.query })
                  .then(() => next())
                  .catch((err) => {
                    handleErrors(err, next, 'Applications');
                  });
              },
            },
            children: [
              {
                path: 'create',
                name: 'org.apps.create',
                component: () => import(/* webpackChunkName: "apps" */ '@/views/applications/create.vue'),
                meta: { permission: 'add-applications' },
              },
            ],
          },
          {
            path: 'apps/:id',
            name: 'org.apps.show',
            component: () => import(/* webpackChunkName: "apps" */ '@/views/applications/show.vue'),
            meta: {
              permission: 'read-applications',
              beforeResolve(to, from, next) {
                store
                  .dispatch('applications/read', { id: to.params.id })
                  .then((application) => {
                    to.params.application = application;
                    next();
                  })
                  .catch((err) => {
                    handleErrors(err, next, 'Application');
                  });
              },
            },
            props: (route) => ({ application: route.params.application }),
          },

          // Webhooks
          {
            path: 'webhooks',
            name: 'org.webhooks.index',
            component: () => import(/* webpackChunkName: "webhooks" */ '@/views/webhooks/index.vue'),
            meta: {
              permission: 'browse-webhooks',
              storeQuery: true,
              beforeResolve(to, from, next) {
                store
                  .dispatch('webhooks/fetch', { query: to.query })
                  .then(() => next())
                  .catch((err) => {
                    handleErrors(err, next, 'Webhooks');
                  });
              },
            },
            children: [
              {
                path: 'create',
                name: 'org.webhooks.create',
                component: () => import(/* webpackChunkName: "webhooks" */ '@/views/webhooks/create.vue'),
                meta: { permission: 'add-webhooks' },
              },
            ],
          },
          {
            path: 'webhooks/:id',
            name: 'org.webhooks.show',
            component: () => import(/* webpackChunkName: "webhooks" */ '@/views/webhooks/show.vue'),
            meta: {
              permission: 'read-webhooks',
              beforeResolve(to, from, next) {
                store
                  .dispatch('webhooks/read', { id: to.params.id })
                  .then((webhook) => {
                    to.params.webhook = webhook;
                    next();
                  })
                  .catch((err) => {
                    handleErrors(err, next, 'Webhook');
                  });
              },
            },
            props: (route) => ({ webhook: route.params.webhook }),
          },

          {
            path: 'authentication',
            name: 'org.authentication.index',
            component: () => import(/* webpackChunkName: "authentication" */ '@/views/sso/index.vue'),
            meta: {
              permission: 'browse-authentication',
            },
          },

          // AUTODETECT (previously filter)
          {
            path: 'filter',
            name: 'org.filter.index',
            component: () => import(/* webpackChunkName: "autodetect" */ '@/views/autodetect/index.vue'),
            meta: {
              permission: 'browse-traxo-filter',
              beforeResolve(to, from, next) {
                store
                  .dispatch('autodetect/index')
                  .then(() => {
                    next();
                  })
                  .catch((err) => {
                    handleErrors(err, next, 'AutoDetect');
                  });
              },
            },
          },
        ],
      },
    ],
  },
];
